// src/features/imprint/stores/useSelectionStore.js

import { defineStore } from 'pinia';

// SessionStorage persistence constants
const STORAGE_KEY = 'selection_state_v1';
const STORAGE_VERSION = 1;
const STORAGE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * @typedef {Object} SelectedColor
 * NOTE: "slectedColor" spelling is intentional for backward compatibility with addToCart
 * @property {string|number} id - Color variant ID
 * @property {string} title - Color title/name
 * @property {string} img_src - Image URL
 * @property {string|number} img_id - Image ID (CRITICAL for cart metaData)
 * @property {string} img_sku - Product SKU (CRITICAL for addToCart)
 * @property {string} [img_gid] - Global ID (optional)
 * @property {string} [alt] - Alt text for image
 * @property {string} [hex] - Hex color code
 * @property {string} [sku] - Variant SKU (alternative to img_sku)
 */

/**
 * @typedef {Object} ColorSizeLine
 * @property {string|number} id - Unique line identifier
 * @property {string} imprintKey - Imprint method key
 * @property {SelectedColor} slectedColor - Selected color (NOTE: typo is intentional!)
 * @property {Object.<string, number>} selectedSizes - Selected sizes with quantities {size: qty}
 * @property {number} [totalQty] - Total quantity for this line
 * @property {string} [randID] - Random ID for uniqueness
 */

/**
 * @typedef {Object} SelectionStore
 * Store for managing selected colors, sizes and quantities
 */

export const useSelectionStore = defineStore('selection', {
    state: () => ({
        // Main data structures for "lines" (Step 2)
        
        /**
         * Color-size-quantity lines, grouped by imprintKey
         * @type {Object.<string, ColorSizeLine[]>}
         * 
         * IMPORTANT: Uses "slectedColor" and "selectedSizes" (note the typo in "slected")
         * This is intentional for backward compatibility with addToCart in StepNavigation.vue
         * 
         * Structure: 
         * {
         *   'embroidery': [
         *     {
         *       id: 1,
         *       imprintKey: 'embroidery',
         *       slectedColor: {
         *         id: 51975323156769,
         *         title: 'red',
         *         img_src: 'https://cdn.shopify.com/...',
         *         img_id: 52988245508385,
         *         img_sku: 'D640W_XL_0004',
         *         img_gid: 'gid://shopify/...',
         *         alt: 'Red shirt',
         *         hex: '#FF0000'
         *       },
         *       selectedSizes: {'S': 10, 'M': 20, 'L': 15},
         *       totalQty: 45,
         *       randID: 'abc123'
         *     }
         *   ]
         * }
         */
        colorSizeLines: {},

        /**
         * List of selected color IDs by imprintKey
         * @type {Object.<string, Array<string|number>>}
         * 
         * Structure:
         * {
         *   'embroidery': [51975323156769, 51975323156770]
         * }
         */
        selectedColorsList: {},

        /**
         * Total quantity by sizes for each imprintKey
         * @type {Object.<string, Object.<string, number>>}
         * 
         * Structure:
         * {
         *   'embroidery': {
         *     'S': 25,
         *     'M': 50,
         *     'L': 30
         *   }
         * }
         */
        totalSizesQty: {},

        /**
         * List of available color variant IDs for selection
         * @type {Array<string|number>}
         * 
         * This is the general list of product color IDs from which user can choose
         */
        availableColorsList: [],

        /**
         * Mapping of full size names to short ones
         * @type {Object.<string, string>}
         * Example: {'Small': 'S', 'Medium': 'M', 'Large': 'L'}
         */
        fullSizeNameToShort: {},

        /**
         * Whether full size names are used
         * @type {boolean}
         */
        hasFullSizeNames: false,

        /**
         * Messages with color lines for imprint
         * @type {Array}
         */
        imprintMessageColorLines: [],

        /**
         * Data for "Apply to All" functionality
         * @type {Object}
         */
        // Todo check duplicate at useNewImprintStore
        applyToAll: {
            id: null,
            selectedColor: null,
            index: null,
            randID: null
        },

        /**
         * Cached API data to avoid redundant requests
         * @type {Object}
         */
        cachedApiData: {
            colorVariants: [],
            sizeVariants: [],
            colorHandle: null,
            sizeHandle: null,
            associatedIds: null,
            isLoaded: false
        },

        /**
         * Special instructions from Step 4 (Review)
         * @type {string}
         */
        specialInstructions: ''
    }),

    getters: {
        /**
         * Get all lines for a specific imprintKey
         * @param {Object} state
         * @returns {function(string): ColorSizeLine[]}
         */
        getLinesByImprintKey: (state) => (imprintKey) => {
            return state.colorSizeLines[imprintKey] || [];
        },

        /**
         * Get selected color IDs for a specific imprintKey
         * @param {Object} state
         * @returns {function(string): Array<string|number>}
         */
        getSelectedColorsByImprintKey: (state) => (imprintKey) => {
            return state.selectedColorsList[imprintKey] || [];
        },

        /**
         * Get total quantity by sizes for imprintKey
         * @param {Object} state
         * @returns {function(string): Object.<string, number>}
         */
        getTotalSizesQtyByImprintKey: (state) => (imprintKey) => {
            return state.totalSizesQty[imprintKey] || {};
        },

        /**
         * Calculate total quantity for all sizes for imprintKey
         * @param {Object} state
         * @returns {function(string): number}
         */
        getTotalQtyByImprintKey: (state) => (imprintKey) => {
            const sizesQty = state.totalSizesQty[imprintKey] || {};
            return Object.values(sizesQty).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
        },

        /**
         * Get count of unique colors for imprintKey
         * @param {Object} state
         * @returns {function(string): number}
         */
        getUniqueColorsCountByImprintKey: (state) => (imprintKey) => {
            const lines = state.colorSizeLines[imprintKey] || [];
            const uniqueColors = new Set(lines.map(line => line.slectedColor?.id).filter(Boolean));
            return uniqueColors.size;
        },

        /**
         * Check if color is selected in the list for imprintKey
         * @param {Object} state
         * @returns {function(string, string|number): boolean}
         */
        hasColorSelected: (state) => (imprintKey, colorId) => {
            const selectedColors = state.selectedColorsList[imprintKey] || [];
            return selectedColors.includes(colorId);
        },

        /**
         * Get all available sizes from all lines for imprintKey
         * @param {Object} state
         * @returns {function(string): string[]}
         */
        getAvailableSizesByImprintKey: (state) => (imprintKey) => {
            const lines = state.colorSizeLines[imprintKey] || [];
            const sizesSet = new Set();
            
            lines.forEach(line => {
                if (line.selectedSizes && typeof line.selectedSizes === 'object') {
                    Object.keys(line.selectedSizes).forEach(size => sizesSet.add(size));
                }
            });
            
            return Array.from(sizesSet);
        },

        /**
         * Check if there is data for Step 2 for a specific imprintKey
         * @param {Object} state
         * @returns {function(string): boolean}
         */
        hasStep2DataForImprintKey: (state) => (imprintKey) => {
            const lines = state.colorSizeLines[imprintKey] || [];
            const hasData = lines.length > 0;
            console.log('[useSelectionStore] hasStep2DataForImprintKey(' + imprintKey + '):', hasData, 'lines.length:', lines.length);
            return hasData;
        }
    },

    actions: {
        /**
         * Check and remove data from storage using a print method key for which no data is selected.
         * @param {string} previousImprintMethod
         */
        removeEmptyImprintMethodData(previousImprintMethod) {
            const hasData = this.hasStep2DataForImprintKey(previousImprintMethod);
            if (!hasData) {
                this.clearLines(previousImprintMethod);
            }
        },

        /**
         * Initialize structures for a new imprintKey
         * @param {string} imprintKey
         * @param {string} [previousImprintKey] - Previous imprint key to copy data from (HOTFIX)
         */
        initImprintKey(imprintKey, previousImprintKey = null) {
            // HOTFIX: Copy data from previous imprint key if provided
            if (previousImprintKey && this.colorSizeLines[previousImprintKey]) {
                // Deep copy lines and update imprintKey
                this.colorSizeLines[imprintKey] = JSON.parse(
                    JSON.stringify(this.colorSizeLines[previousImprintKey])
                ).map(line => ({ ...line, imprintKey }));
                
                // Copy selected colors list
                this.selectedColorsList[imprintKey] = [...(this.selectedColorsList[previousImprintKey] || [])];
                
                // Copy total sizes quantity
                this.totalSizesQty[imprintKey] = { ...(this.totalSizesQty[previousImprintKey] || {}) };
            } else {
                // Original logic: create empty structures
                if (!this.colorSizeLines[imprintKey]) {
                    this.colorSizeLines[imprintKey] = [];
                }
                if (!this.selectedColorsList[imprintKey]) {
                    this.selectedColorsList[imprintKey] = [];
                }
                if (!this.totalSizesQty[imprintKey]) {
                    this.totalSizesQty[imprintKey] = {};
                }
            }
        },

        /**
         * Add a new color-size-quantity line
         * @param {string} imprintKey
         * @param {ColorSizeLine} line
         */
        addColorSizeLine(imprintKey, line) {
            this.initImprintKey(imprintKey);
            
            // Validate critical fields
            if (line.slectedColor && !line.slectedColor.img_sku) {
                console.warn('[useSelectionStore] addColorSizeLine: img_sku is missing!');
            }
            if (line.slectedColor && !line.slectedColor.img_id) {
                console.warn('[useSelectionStore] addColorSizeLine: img_id is missing!');
            }
            
            // Generate randID if it doesn't exist
            if (!line.randID) {
                line.randID = `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            
            // Generate ID if it doesn't exist
            if (!line.id) {
                const existingLines = this.colorSizeLines[imprintKey];
                line.id = existingLines.length > 0 
                    ? Math.max(...existingLines.map(l => Number(l.id) || 0)) + 1 
                    : 1;
            }
            
            // Ensure proper structure
            if (!line.selectedSizes) {
                line.selectedSizes = {};
            }
            
            this.colorSizeLines[imprintKey].push(line);
            this.recalculateTotalSizesQty(imprintKey);
            this.updateSelectedColorsList(imprintKey);
        },

        /**
         * Update existing line
         * @param {string} imprintKey
         * @param {string|number} lineId
         * @param {Partial<ColorSizeLine>} updates
         */
        updateColorSizeLine(imprintKey, lineId, updates) {
            const lines = this.colorSizeLines[imprintKey] || [];
            const index = lines.findIndex(line => line.id === lineId);
            
            if (index !== -1) {
                this.colorSizeLines[imprintKey][index] = {
                    ...lines[index],
                    ...updates
                };
                this.recalculateTotalSizesQty(imprintKey);
                this.updateSelectedColorsList(imprintKey);
            }
        },

        /**
         * Remove line
         * @param {string} imprintKey
         * @param {string|number} lineId
         */
        removeColorSizeLine(imprintKey, lineId) {
            if (this.colorSizeLines[imprintKey]) {
                this.colorSizeLines[imprintKey] = this.colorSizeLines[imprintKey]
                    .filter(line => line.id !== lineId);
                this.recalculateTotalSizesQty(imprintKey);
                this.updateSelectedColorsList(imprintKey);
            }
        },

        /**
         * Clear all lines for imprintKey
         * @param {string} imprintKey
         */
        clearLines(imprintKey) {

            // Delete the key completely - Vue 3 reactive way
            if (this.colorSizeLines[imprintKey] !== undefined) {
                delete this.colorSizeLines[imprintKey];
            }
            if (this.selectedColorsList[imprintKey] !== undefined) {
                delete this.selectedColorsList[imprintKey];
            }
            if (this.totalSizesQty[imprintKey] !== undefined) {
                delete this.totalSizesQty[imprintKey];
            }
        },

        /**
         * Recalculate total quantity by sizes for imprintKey
         * NOTE: Uses "selectedSizes" field name
         * @param {string} imprintKey
         */
        recalculateTotalSizesQty(imprintKey) {
            const lines = this.colorSizeLines[imprintKey] || [];
            const totals = {};
            
            lines.forEach(line => {
                if (line.selectedSizes && typeof line.selectedSizes === 'object') {
                    Object.entries(line.selectedSizes).forEach(([size, qty]) => {
                        const numQty = Number(qty) || 0;
                        totals[size] = (totals[size] || 0) + numQty;
                    });
                }
            });
            
            this.totalSizesQty[imprintKey] = totals;
        },

        /**
         * Update list of selected color IDs based on lines
         * NOTE: Uses "slectedColor" field name
         * @param {string} imprintKey
         */
        updateSelectedColorsList(imprintKey) {
            const lines = this.colorSizeLines[imprintKey] || [];
            const uniqueColorIds = new Set();
            
            lines.forEach(line => {
                if (line.slectedColor && line.slectedColor.id) {
                    uniqueColorIds.add(line.slectedColor.id);
                }
            });
            
            this.selectedColorsList[imprintKey] = Array.from(uniqueColorIds);
        },

        /**
         * Set list of available color variant IDs
         * @param {Array<string|number>} colorIds
         */
        setAvailableColorsList(colorIds) {
            this.availableColorsList = colorIds || [];
        },

        /**
         * Set mapping of full size names
         * @param {Object.<string, string>} mapping
         */
        setFullSizeNameMapping(mapping) {
            this.fullSizeNameToShort = mapping || {};
            this.hasFullSizeNames = Object.keys(mapping || {}).length > 0;
        },

        /**
         * Apply settings to all lines (Apply to All)
         * @param {string} imprintKey
         * @param {Object} applyToAllData
         */
        setApplyToAll(imprintKey, applyToAllData) {
            this.applyToAll = {
                ...this.applyToAll,
                ...applyToAllData
            };
        },

        /**
         * Clear Apply to All data
         */
        clearApplyToAll() {
            this.applyToAll = {
                id: null,
                selectedColor: null,
                index: null,
                randID: null
            };
        },

        /**
         * Update quantity for a specific size in line
         * NOTE: Uses "selectedSizes" field name
         * @param {string} imprintKey
         * @param {string|number} lineId
         * @param {string} size
         * @param {number} qty
         */
        updateLineSizeQty(imprintKey, lineId, size, qty) {
            const lines = this.colorSizeLines[imprintKey] || [];
            const line = lines.find(l => l.id === lineId);
            
            if (line) {
                if (!line.selectedSizes) {
                    line.selectedSizes = {};
                }
                
                line.selectedSizes[size] = Number(qty) || 0;
                
                // Recalculate totalQty for the line
                line.totalQty = Object.values(line.selectedSizes)
                    .reduce((sum, q) => sum + (Number(q) || 0), 0);
                
                this.recalculateTotalSizesQty(imprintKey);
            }
        },

        /**
         * Set messages with color lines
         * @param {Array} messages
         */
        setImprintMessageColorLines(messages) {
            this.imprintMessageColorLines = messages || [];
        },

        /**
         * Set cached API data
         * @param {Object} data
         */
        setCachedApiData(data) {
            this.cachedApiData = {
                ...this.cachedApiData,
                ...data,
                isLoaded: true
            };
        },

        /**
         * Get cached API data
         * @returns {Object}
         */
        getCachedApiData() {
            return this.cachedApiData;
        },

        /**
         * Check if API data is already loaded
         * @returns {boolean}
         */
        isApiDataLoaded() {
            return this.cachedApiData.isLoaded;
        },

        /**
         * Set special instructions
         * @param {string} instructions
         */
        setSpecialInstructions(instructions) {
            this.specialInstructions = instructions || '';
            console.log('[useSelectionStore] Special instructions set:', instructions);
        },

        /**
         * Reset entire store to initial state
         */
        $reset() {
            this.colorSizeLines = {};
            this.selectedColorsList = {};
            this.totalSizesQty = {};
            this.availableColorsList = [];
            this.fullSizeNameToShort = {};
            this.hasFullSizeNames = false;
            this.imprintMessageColorLines = [];
            this.applyToAll = {
                id: null,
                selectedColor: null,
                index: null,
                randID: null
            };
            this.cachedApiData = {
                colorVariants: [],
                sizeVariants: [],
                colorHandle: null,
                sizeHandle: null,
                associatedIds: null,
                isLoaded: false
            };
            this.specialInstructions = '';
        },

        // ====================================
        // SESSION STORAGE PERSISTENCE
        // ====================================

        /**
         * Save selection state to sessionStorage
         * Called before redirect to preserve user's color & quantity selections
         */
        saveToSessionStorage() {
            try {
                const data = {
                    version: STORAGE_VERSION,
                    timestamp: Date.now(),
                    productKey: window.location.pathname,  // ✅ NEW: Save product pathname for validation
                    colorSizeLines: this.colorSizeLines,
                    selectedColorsList: this.selectedColorsList,
                    totalSizesQty: this.totalSizesQty,
                    availableColorsList: this.availableColorsList,
                    fullSizeNameToShort: this.fullSizeNameToShort,
                    hasFullSizeNames: this.hasFullSizeNames,
                    specialInstructions: this.specialInstructions
                    // NOTE: We DON'T save:
                    // - cachedApiData (will reload from product)
                    // - imprintMessageColorLines (temporary)
                    // - applyToAll (UI state)
                };
                
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                console.log('[useSelectionStore] 💾 State saved to sessionStorage:', {
                    colorLines: Object.keys(data.colorSizeLines).length,
                    totalColors: Object.values(data.selectedColorsList).flat().length
                });
                
                return true;
            } catch (error) {
                console.error('[useSelectionStore] ❌ Failed to save to sessionStorage:', error);
                return false;
            }
        },

        /**
         * Restore selection state from sessionStorage
         * Called on page load to recover user data after redirect
         */
        restoreFromSessionStorage() {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                if (!raw) {
                    console.log('[useSelectionStore] No saved state found in sessionStorage');
                    return false;
                }
                
                const data = JSON.parse(raw);
                
                // Version check
                if (data.version !== STORAGE_VERSION) {
                    console.warn('[useSelectionStore] ⚠️ Storage version mismatch');
                    this.clearSessionStorage();
                    return false;
                }
                
                // ✅ CRITICAL: Product key check (prevent restoring data from different product)
                const currentProductKey = window.location.pathname;
                if (data.productKey && data.productKey !== currentProductKey) {
                    console.warn('[useSelectionStore] ⚠️ Product key mismatch:', {
                        stored: data.productKey,
                        current: currentProductKey
                    });
                    console.log('[useSelectionStore] Clearing storage from different product');
                    this.clearSessionStorage();
                    return false;
                }
                
                // TTL check (30 minutes)
                const age = Date.now() - data.timestamp;
                if (age > STORAGE_TTL) {
                    console.warn('[useSelectionStore] ⚠️ Storage data expired (age:', Math.round(age / 1000), 'seconds)');
                    this.clearSessionStorage();
                    return false;
                }
                
                // Restore data
                this.colorSizeLines = data.colorSizeLines || {};
                this.selectedColorsList = data.selectedColorsList || {};
                this.totalSizesQty = data.totalSizesQty || {};
                this.availableColorsList = data.availableColorsList || [];
                this.fullSizeNameToShort = data.fullSizeNameToShort || {};
                this.hasFullSizeNames = data.hasFullSizeNames || false;
                this.specialInstructions = data.specialInstructions || '';
                
                console.log('[useSelectionStore] ✅ State restored from sessionStorage:', {
                    colorLines: Object.keys(this.colorSizeLines).length,
                    totalColors: Object.values(this.selectedColorsList).flat().length,
                    age: Math.round(age / 1000) + 's ago'
                });
                
                return true;
            } catch (error) {
                console.error('[useSelectionStore] ❌ Failed to restore from sessionStorage:', error);
                this.clearSessionStorage();
                return false;
            }
        },

        /**
         * Clear saved state from sessionStorage
         */
        clearSessionStorage() {
            try {
                sessionStorage.removeItem(STORAGE_KEY);
                console.log('[useSelectionStore] 🗑️ SessionStorage cleared');
            } catch (error) {
                console.error('[useSelectionStore] ❌ Failed to clear sessionStorage:', error);
            }
        }
    }
});
